import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabase';
import { getProductByPriceId } from '../stripe-config';
import { stripeProducts } from '../stripe-config';

export interface SubscriptionFeatures {
  // Document limits
  maxDocuments: number;
  maxDocumentSize: number; // in characters
  
  // AI Features
  aiProcessing: 'basic' | 'advanced' | 'premium';
  customTemplates: boolean;
  advancedFormatting: boolean;
  
  // Collaboration
  teamCollaboration: boolean;
  realTimeEditing: boolean;
  commentSystem: boolean;
  
  // Support & Analytics
  supportLevel: 'email' | 'priority' | 'dedicated';
  analytics: boolean;
  versionHistory: boolean;
  
  // Export & Integration
  exportFormats: string[];
  apiAccess: boolean;
  whiteLabel: boolean;
  
  // Storage & Backup
  cloudStorage: number; // in GB
  autoBackup: boolean;
}

export interface UserSubscription {
  plan: 'free' | 'basic' | 'ai' | 'team';
  status: string;
  features: SubscriptionFeatures;
  priceId?: string;
  expiresAt?: Date;
}

const PLAN_FEATURES: Record<string, SubscriptionFeatures> = {
  free: {
    maxDocuments: 5,
    maxDocumentSize: 10000,
    aiProcessing: 'basic',
    customTemplates: false,
    advancedFormatting: false,
    teamCollaboration: false,
    realTimeEditing: false,
    commentSystem: true,
    supportLevel: 'email',
    analytics: false,
    versionHistory: false,
    exportFormats: ['txt', 'pdf'],
    apiAccess: false,
    whiteLabel: false,
    cloudStorage: 1,
    autoBackup: false,
  },
  basic: {
    maxDocuments: 50,
    maxDocumentSize: 50000,
    aiProcessing: 'basic',
    customTemplates: true,
    advancedFormatting: true,
    teamCollaboration: true,
    realTimeEditing: false,
    commentSystem: true,
    supportLevel: 'email',
    analytics: false,
    versionHistory: true,
    exportFormats: ['txt', 'pdf', 'docx', 'md'],
    apiAccess: false,
    whiteLabel: false,
    cloudStorage: 5,
    autoBackup: true,
  },
  ai: {
    maxDocuments: -1, // unlimited
    maxDocumentSize: 100000,
    aiProcessing: 'advanced',
    customTemplates: true,
    advancedFormatting: true,
    teamCollaboration: true,
    realTimeEditing: true,
    commentSystem: true,
    supportLevel: 'priority',
    analytics: true,
    versionHistory: true,
    exportFormats: ['txt', 'pdf', 'docx', 'md', 'json', 'html'],
    apiAccess: false,
    whiteLabel: false,
    cloudStorage: 25,
    autoBackup: true,
  },
  team: {
    maxDocuments: -1, // unlimited
    maxDocumentSize: -1, // unlimited
    aiProcessing: 'premium',
    customTemplates: true,
    advancedFormatting: true,
    teamCollaboration: true,
    realTimeEditing: true,
    commentSystem: true,
    supportLevel: 'dedicated',
    analytics: true,
    versionHistory: true,
    exportFormats: ['txt', 'pdf', 'docx', 'md', 'json', 'html', 'xml', 'rtf'],
    apiAccess: true,
    whiteLabel: true,
    cloudStorage: 100,
    autoBackup: true,
  },
};

export const useSubscription = () => {
  const { user } = useAuthStore();
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadSubscription();
      
      // Listen for subscription refresh events
      const handleRefreshSubscription = () => {
        console.log('🔄 Received subscription refresh event');
        console.log('🕐 Refresh event timestamp:', new Date().toISOString());
        
        // Immediate refresh
        loadSubscription();
        
        // Also refresh after a short delay in case of timing issues
        setTimeout(() => {
          console.log('🔄 Delayed subscription refresh...');
          loadSubscription();
        }, 2000);
      };
      
      window.addEventListener('refreshSubscription', handleRefreshSubscription);
      
      return () => {
        window.removeEventListener('refreshSubscription', handleRefreshSubscription);
      };
    } else {
      setSubscription(null);
      setLoading(false);
    }
  }, [user]);

  const loadSubscription = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 SUBSCRIPTION LOAD START for user:', user?.id);
      console.log('🕐 Load timestamp:', new Date().toISOString());

      // STEP 1: Try to get subscription data from view
      // STEP 1: Get user's customer record
      console.log('👤 STEP 1: Getting customer record for user...');
      const { data: customerData, error: customerError } = await supabase
        .from('stripe_customers')
        .select('customer_id')
        .eq('user_id', user?.id)
        .maybeSingle();

      console.log('👤 CUSTOMER QUERY RESULT:', customerData);
      console.log('❌ CUSTOMER QUERY ERROR:', customerError);

      if (customerError || !customerData?.customer_id) {
        console.log('❌ No customer found, defaulting to free plan');
        setSubscription({
          plan: 'free',
          status: 'active',
          features: PLAN_FEATURES.free,
        });
        setLoading(false);
        return;
      }

      // STEP 2: Get ALL subscriptions to see what's available
      console.log('📊 STEP 2: Getting ALL subscriptions...');
      const { data: allSubs, error: allSubsError } = await supabase
        .from('stripe_subscriptions')
        .select('*');
      
      console.log('📊 ALL SUBSCRIPTIONS IN TABLE:', allSubs);
      console.log('❌ ALL SUBSCRIPTIONS ERROR:', allSubsError);
      
      // STEP 3: Find subscription for this customer
      console.log('🔍 STEP 3: Looking for subscription for customer:', customerData.customer_id);
      const { data: directSubData, error: directSubError } = await supabase
        .from('stripe_subscriptions')
        .select('*')
        .eq('customer_id', customerData.customer_id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      console.log('📊 DIRECT SUBSCRIPTION QUERY RESULT:', directSubData);
      console.log('❌ DIRECT SUBSCRIPTION ERROR:', directSubError);

      let subData = null;
      if (!directSubError && directSubData) {
        console.log('✅ FOUND SUBSCRIPTION DATA');
        // Map the direct subscription data to the expected format
        subData = {
          customer_id: directSubData.customer_id,
          subscription_id: directSubData.subscription_id,
          subscription_status: directSubData.status,
          price_id: directSubData.price_id,
          current_period_start: directSubData.current_period_start,
          current_period_end: directSubData.current_period_end,
          cancel_at_period_end: directSubData.cancel_at_period_end,
          payment_method_brand: directSubData.payment_method_brand,
          payment_method_last4: directSubData.payment_method_last4
        };
        console.log('🔄 MAPPED SUBSCRIPTION DATA:', subData);
      } else {
        console.log('❌ No subscription data found');
        setSubscription({
          plan: 'free',
          status: 'active',
          features: PLAN_FEATURES.free,
        });
        setLoading(false);
        return;
      }

      // STEP 3: Process subscription data
      let planType: 'free' | 'basic' | 'ai' | 'team' = 'free';
      let status = 'active';
      let priceId: string | undefined;
      let expiresAt: Date | undefined;

      console.log('🔍 STEP 3: Processing subscription data...');
      console.log('🔍 Checking subscription validity:', {
        hasData: !!subData,
        hasStatus: !!subData?.subscription_status,
        status: subData?.subscription_status,
        hasPriceId: !!subData?.price_id,
        priceId: subData?.price_id
      });

      // Check for ANY subscription status with a price_id
      if (subData && subData.subscription_status && subData.price_id && 
          ['active', 'trialing', 'past_due'].includes(subData.subscription_status)) {
        console.log('🎯 VALID SUBSCRIPTION FOUND:', {
          status: subData.subscription_status,
          priceId: subData.price_id,
          customerId: subData.customer_id
        });
        
        const product = getProductByPriceId(subData.price_id);
        console.log('🏷️ PRODUCT LOOKUP RESULT:', product);
        
        if (product) {
          console.log('🔍 PLAN DETECTION for product:', product.name);
          
          const productNameLower = product.name.toLowerCase();
          
          if (productNameLower.includes('basic') || subData.price_id === 'price_1RfRUSP7CWjQ0SEIKKJELyB8') {
            planType = 'basic';
            console.log('📋 PLAN DETECTED: basic');
          } else if (productNameLower.includes('team') || productNameLower.includes('agent') || subData.price_id === 'price_1R2kDeP7CWjQ0SEI5kmQjXZu') {
            planType = 'team';
            console.log('📋 PLAN DETECTED: team');
          } else if (productNameLower.includes('ai') || subData.price_id === 'price_1RfRVgP7CWjQ0SEIoh8ApXB0') {
            planType = 'ai';
            console.log('📋 PLAN DETECTED: ai');
          } else {
            // Default to 'basic' for any paid plan we don't recognize
            planType = 'basic';
            console.log('📋 PLAN DETECTED: basic (default for paid)');
          }
          
          status = subData.subscription_status;
          priceId = subData.price_id;
          
          if (subData.current_period_end) {
            expiresAt = new Date(subData.current_period_end * 1000);
          }
          
          console.log('🎯 FINAL PLAN ASSIGNMENT:', {
            planType,
            status,
            priceId,
            expiresAt: expiresAt?.toISOString()
          });
        } else {
          console.warn('⚠️ NO PRODUCT FOUND for price ID:', subData.price_id);
          console.log('🔍 AVAILABLE PRODUCTS:', stripeProducts.map(p => ({ name: p.name, priceId: p.priceId })));
          
          // If we have a subscription but can't match the product, default to basic plan
          if (subData.subscription_status && subData.subscription_status !== 'not_started') {
            planType = 'basic';
            status = subData.subscription_status;
            priceId = subData.price_id;
            console.log('⚠️ USING FALLBACK BASIC PLAN for unmatched product');
          }
        }
      } else {
        console.log('ℹ️ NO VALID SUBSCRIPTION FOUND, defaulting to free plan');
        console.log('🔍 Subscription data analysis:', {
          hasData: !!subData,
          status: subData?.subscription_status,
          priceId: subData?.price_id,
          isEmpty: !subData || Object.keys(subData).length === 0,
          isValidStatus: subData?.subscription_status ? ['active', 'trialing', 'past_due'].includes(subData.subscription_status) : false
        });
      }

      const userSubscription: UserSubscription = {
        plan: planType,
        status,
        features: PLAN_FEATURES[planType],
        priceId,
        expiresAt,
      };

      console.log('🎉 FINAL SUBSCRIPTION OBJECT:', userSubscription);
      console.log('🔍 PLAN FEATURES:', PLAN_FEATURES[planType]);
      setSubscription(userSubscription);
      
      console.log('🎯 SUBSCRIPTION STATE SET:', {
        plan: userSubscription.plan,
        status: userSubscription.status,
        aiLevel: userSubscription.features.aiProcessing,
        maxDocuments: userSubscription.features.maxDocuments,
        hasAnalytics: userSubscription.features.analytics,
        hasApiAccess: userSubscription.features.apiAccess
      });
      
      console.log('✅ SUBSCRIPTION LOAD COMPLETED SUCCESSFULLY');
    } catch (err: any) {
      console.error('❌ SUBSCRIPTION LOAD ERROR:', err);
      console.error('🔍 Full error object:', JSON.stringify(err, null, 2));
      setError(err.message);
      
      console.log('🆓 FALLBACK: Setting free plan due to error');
      setSubscription({
        plan: 'free',
        status: 'active',
        features: PLAN_FEATURES.free,
      });
    } finally {
      setLoading(false);
    }
  };

  const hasFeature = (feature: keyof SubscriptionFeatures): boolean => {
    if (!subscription) return false;
    return Boolean(subscription.features[feature]);
  };

  const canCreateDocument = async (): Promise<boolean> => {
    if (!subscription || !user) return false;
    
    if (subscription.features.maxDocuments === -1) return true;
    
    try {
      const { count, error } = await supabase
        .from('documents')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      if (error) throw error;
      
      return (count || 0) < subscription.features.maxDocuments;
    } catch (error) {
      console.error('Error checking document count:', error);
      return false;
    }
  };

  const canUseAIFeature = (feature: 'basic' | 'advanced' | 'premium'): boolean => {
    if (!subscription) return false;
    
    console.log('🤖 AI FEATURE CHECK:', {
      requestedFeature: feature,
      userPlan: subscription.plan,
      userAILevel: subscription.features.aiProcessing,
      timestamp: new Date().toISOString()
    });
    
    const levels = ['basic', 'advanced', 'premium'];
    const userLevel = levels.indexOf(subscription.features.aiProcessing);
    const requiredLevel = levels.indexOf(feature);
    
    const canUse = userLevel >= requiredLevel;
    
    console.log('🎯 AI FEATURE RESULT:', {
      userLevel: subscription.features.aiProcessing,
      requiredLevel: feature,
      userLevelIndex: userLevel,
      requiredLevelIndex: requiredLevel,
      canUse
    });
    
    return canUse;
  };

  const getDocumentLimit = (): number => {
    return subscription?.features.maxDocuments || 0;
  };

  const getStorageLimit = (): number => {
    return subscription?.features.cloudStorage || 0;
  };

  return {
    subscription,
    loading,
    error,
    hasFeature,
    canCreateDocument,
    canUseAIFeature,
    getDocumentLimit,
    getStorageLimit,
    refresh: loadSubscription,
  };
};